import { Component, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { firstValueFrom } from 'rxjs';
import { ActivityLogService } from './activity-log.service';
import { TableActivityLogComponent } from '../table-activity-log/table-activity-log.component';

type UserItem = { email: string; name: string; active: boolean };

@Component({
  selector: 'app-activity-log',
  templateUrl: './activity-log.component.html',
  styleUrls: ['./activity-log.component.scss'],
})
export class ActivityLogComponent {
  startRaw = '';
  endRaw = '';

  filters = new FormGroup({
    userId: new FormControl<string>(''),
    type: new FormControl<string>(''),
    initialDate: new FormControl<Date | null>(null, {
      validators: [Validators.required],
      updateOn: 'blur',
    }),
    endDate: new FormControl<Date | null>(null, {
      validators: [Validators.required],
      updateOn: 'blur',
    }),
  });

  public users: UserItem[] = [];
  public isFilterCollapsed = false;
  public isLoaded = false;
  public usersSelect: any[] = [{ label: 'Todos', value: '' }];

  @ViewChild(TableActivityLogComponent) table!: TableActivityLogComponent;

  constructor(
    private activityLogService: ActivityLogService,
    private toastr: ToastrService
  ) { }

  private pad(n: number): string {
    return String(n).padStart(2, '0');
  }

  private formatBr(d: Date): string {
    return `${this.pad(d.getDate())}/${this.pad(d.getMonth() + 1)}/${d.getFullYear()}`;
  }

  async ngOnInit(): Promise<void> {
    const now = new Date();
    const today = new Date(now);
    today.setHours(0, 0, 0, 0);

    this.filters.controls.initialDate.setValue(today);
    this.filters.controls.endDate.setValue(today);

    this.startRaw = this.formatBr(today);
    this.endRaw = this.formatBr(today);

    this.filters.controls.type.valueChanges.subscribe((v) => {
      const up = (v ?? '').toUpperCase();
      if (v !== up) this.filters.controls.type.setValue(up, { emitEvent: false });
    });

    try {
      const resp = await firstValueFrom(this.activityLogService.getUsers());
      const list = (resp?.users ?? []).filter((u: any) => !!u?.active);

      this.usersSelect = [
        { label: 'Todos', value: '' },
        ...list.map((u: any) => ({
          label: `${u.email} (${u.name})`,
          value: u.email,
        })),
      ];
    } catch {
      this.usersSelect = [{ label: 'Todos', value: '' }];
    }
  }

  onLogsLoaded(): void {
    this.isLoaded = true;
  }

  private asDate(v: any): Date {
    return v?.toDate ? v.toDate() : (v as Date);
  }

  private ymd(d: Date): string {
    return `${d.getFullYear()}-${this.pad(d.getMonth() + 1)}-${this.pad(d.getDate())}`;
  }

  private toIsoStart(d: Date): string {
    return `${this.ymd(d)}T00:00:00`;
  }

  private toIsoEnd(d: Date): string {
    return `${this.ymd(d)}T23:59:59`;
  }

  private parseDate(s: string): Date | null {
    if (!s) return null;
    const m = s.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (!m) return null;
    const d = new Date(+m[3], +m[2] - 1, +m[1], 0, 0, 0, 0);
    return isNaN(d.getTime()) ? null : d;
  }

  parseDateInput(ctrlName: 'initialDate' | 'endDate', raw: string): Date | null {
    const ctrl = this.filters.controls[ctrlName];

    const parsed = this.parseDate(raw);
    if (parsed) {
      ctrl.setValue(parsed, { emitEvent: false });
      ctrl.setErrors(null);
      return parsed;
    }

    if (raw?.trim()) {
      ctrl.setErrors({ matDatepickerParse: { text: raw } });
    } else {
      ctrl.setValue(null, { emitEvent: false });
      ctrl.setErrors({ required: true });
    }

    return null;
  }

  onDateChange(ctrlName: 'initialDate' | 'endDate', raw: string): void {
    const parsed = this.parseDateInput(ctrlName, raw);
    if (!parsed) return;

    const normalized = this.formatBr(parsed);
    if (ctrlName === 'initialDate') this.startRaw = normalized;
    else this.endRaw = normalized;
  }

  private fixDatesFromUI(startRaw?: string, endRaw?: string): void {
    if (startRaw !== undefined) this.parseDateInput('initialDate', startRaw);
    if (endRaw !== undefined) this.parseDateInput('endDate', endRaw);
  }

  buscar(startRaw?: string, endRaw?: string): void {
    this.fixDatesFromUI(startRaw, endRaw);

    this.filters.markAllAsTouched();
    this.filters.updateValueAndValidity({ emitEvent: false });

    const start = this.filters.controls.initialDate.value as Date | null;
    const end = this.filters.controls.endDate.value as Date | null;

    if (!start || !end) return;

    if (start > end) {
      this.toastr.error('Data inicial deve ser menor ou igual a data final!', 'Erro', {
        timeOut: 3000,
        positionClass: 'toast-top-right',
      });
      return;
    }

    if (this.filters.invalid) return;

    this.isLoaded = false;

    const startIso = this.toIsoStart(this.asDate(start));
    const endIso = this.toIsoEnd(this.asDate(end));

    const userId = this.filters.controls.userId.value || '';
    const type = this.filters.controls.type.value || '';

    this.table.page = 1;
    this.table.startDateIso = startIso;
    this.table.endDateIso = endIso;
    this.table.userId = userId;
    this.table.type = type;

    this.table.getTransactions();
  }

  toggleFilters(): void {
    this.isFilterCollapsed = !this.isFilterCollapsed;
  }

  async pasteType(): Promise<void> {
    try {
      const text = (await navigator.clipboard.readText()).trim();
      this.filters.controls.type.setValue(text);
    } catch {
      this.toastr.error('Erro ao colar conteúdo.', 'Erro', {
        timeOut: 2000,
        positionClass: 'toast-top-right',
      });
    }
  }
}